import { useEffect } from "react";
import { Clock3, ClipboardCheck, FileText, GitBranch, ShieldCheck, Building2 } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AmbientParticles from "@/components/site/AmbientParticles";
import { AUDIT_IMAGES } from "@/lib/site";

/* ─── Tally form embed loader ─── */
function useTallyEmbed() {
  useEffect(() => {
    const id = "tally-embed-loader";
    if (document.getElementById(id)) {
      if (typeof window.Tally !== "undefined") window.Tally.loadEmbeds();
      return;
    }
    const s = document.createElement("script");
    s.id = id;
    s.src = "https://tally.so/widgets/embed.js";
    s.async = true;
    s.onload = () => { if (typeof window.Tally !== "undefined") window.Tally.loadEmbeds(); };
    document.body.appendChild(s);
  }, []);
}

/* ─── Eyebrow tag (reused styling) ─── */
function Eyebrow({ children }) {
  return (
    <div className="text-[10.5px] tracking-[0.38em] text-sapphire-glow uppercase font-medium">
      {children}
    </div>
  );
}

/* ─── Feature card with sapphire icon ─── */
function FeatureCard({ icon: Icon, title, desc, delay = 0 }) {
  return (
    <div
      className="relative rounded-2xl border border-white/[0.08] p-7 sm:p-8 group hover:border-sapphire-glow/35 transition-colors fade-up"
      style={{
        background: "linear-gradient(180deg, rgba(20,28,48,0.45) 0%, rgba(13,18,32,0.7) 100%)",
        animationDelay: `${delay}ms`,
      }}
    >
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/30 to-transparent" />
      <div className="flex items-start gap-5">
        <div
          className="shrink-0 w-12 h-12 rounded-full inline-flex items-center justify-center"
          style={{
            background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.18), #0E1422 70%)",
            border: "1px solid rgba(96,165,250,0.42)",
            boxShadow: "0 0 18px -3px rgba(96,165,250,0.45)",
          }}
        >
          <Icon className="w-5 h-5 text-sapphire-glow" />
        </div>
        <div>
          <h3 className="font-display text-platinum text-[18px] sm:text-[20px] leading-tight tracking-tight">{title}</h3>
          <p className="mt-2.5 text-[13px] sm:text-[13.5px] text-platinum/65 leading-[1.65]">{desc}</p>
        </div>
      </div>
    </div>
  );
}

export default function AuditReportPage() {
  useTallyEmbed();
  // Scroll to top on mount
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="audit-report-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={30} tone="mixed" density={0.55} />
      </div>
      <Navbar onBookDemo={() => { /* no-op; this page IS the conversion path */ }} />

      {/* ─────── HERO ─────── */}
      <section className="relative pt-[120px] lg:pt-[140px] pb-12 lg:pb-16 overflow-hidden">
        {/* Ambient sapphire backdrop */}
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.07]" aria-hidden />
        <div className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.20), transparent)" }} aria-hidden />

        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.25fr] gap-8 lg:gap-12 items-center min-h-[520px] lg:min-h-[640px]">
            {/* Text */}
            <div className="fade-up relative z-10">
              <h1 className="font-display text-platinum text-[40px] sm:text-[54px] lg:text-[60px] xl:text-[64px] leading-[1.04] tracking-[-0.022em]">
                Find out where your<br />
                luxury inquiries leak revenue.
              </h1>
              <p className="mt-7 text-[15px] sm:text-[16px] text-platinum/70 leading-[1.75] max-w-[560px]">
                A scored diagnostic of how your operation captures, qualifies,
                and converts high-value leads — delivered as a private report.
                <br />
                <span className="text-platinum/55">No cost, no commitment.</span>
              </p>
            </div>

            {/* Crystal-City asset — FULL-BLEED, homepage style */}
            <div className="relative lg:-mr-10 xl:-mr-20" data-testid="audit-hero-visual">
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.32), transparent 72%)", filter: "blur(50px)" }} aria-hidden />

              {/* Twinkling stars */}
              <div className="absolute top-[18%] right-[14%] w-1.5 h-1.5 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.4s", boxShadow: "0 0 12px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute top-[34%] left-[14%] w-1 h-1 rounded-full bg-white twinkle" style={{ animationDelay: "1.2s", boxShadow: "0 0 10px rgba(255,255,255,0.95)" }} aria-hidden />
              <div className="absolute bottom-[32%] right-[24%] w-1 h-1 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "2.0s", boxShadow: "0 0 10px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute bottom-[44%] left-[22%] w-[5px] h-[5px] rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.9s", boxShadow: "0 0 14px rgba(96,165,250,1)" }} aria-hidden />

              <div className="relative">
                <img
                  src={AUDIT_IMAGES.heroCrystalCity}
                  alt="Esthera RealityOS — luxury inquiry audit crystal on the Dubai skyline podium"
                  className="block w-full h-[500px] lg:h-[640px] object-contain object-center breathe-glow slow-float"
                  style={{
                    mixBlendMode: "screen",
                    filter: "brightness(0.95) contrast(2.4) saturate(1.20)",
                    WebkitMaskImage: "radial-gradient(105% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.55) 82%, transparent 100%)",
                    maskImage: "radial-gradient(105% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.55) 82%, transparent 100%)",
                  }}
                  draggable="false"
                />
                {/* Vertical edge fade */}
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)" }} aria-hidden />
                {/* Horizontal edge fade — heavy left fade into copy column */}
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,0.80) 14%, rgba(10,15,26,0.16) 32%, rgba(10,15,26,0) 50%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)" }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── 3 FEATURE CARDS ─────── */}
      <section className="relative pb-14 lg:pb-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-5 lg:gap-6" data-testid="audit-feature-cards">
            <FeatureCard
              icon={ClipboardCheck}
              title="A scored audit"
              desc="Five dimensions of your lead-handling, benchmarked against industry-standard ranges."
              delay={0}
            />
            <FeatureCard
              icon={FileText}
              title="A private report"
              desc="A premium document showing exactly where revenue is leaking and what good looks like."
              delay={120}
            />
            <FeatureCard
              icon={GitBranch}
              title="A clear path"
              desc="The specific fixes that close the gaps, mapped to your operation."
              delay={240}
            />
          </div>
        </div>
      </section>

      {/* ─────── TRUST LINE ─────── */}
      <section className="relative pb-10 lg:pb-14">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10 text-center fade-up">
          <div className="inline-flex items-center gap-3 text-platinum">
            <ShieldCheck className="w-5 h-5 text-sapphire-glow shrink-0" />
            <span className="font-display text-[18px] sm:text-[20px] tracking-tight">
              We&apos;d rather show you the gaps than tell you about them.
            </span>
          </div>
          <p className="mt-3 text-[14px] sm:text-[15px] text-platinum/65 leading-[1.7]">
            The audit is how we prove we understand your business before you spend a rupee.
          </p>
        </div>
      </section>

      {/* ─────── TALLY FORM PANEL ─────── */}
      <section className="relative pb-14 lg:pb-20">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <div
            className="relative rounded-2xl p-6 sm:p-8 lg:p-10 fade-up"
            data-testid="audit-form-panel"
            style={{
              background: "linear-gradient(180deg, rgba(20,28,48,0.45) 0%, rgba(10,15,26,0.85) 100%)",
              border: "1px solid rgba(96,165,250,0.32)",
              boxShadow: "0 0 32px -8px rgba(96,165,250,0.25), inset 0 0 24px rgba(96,165,250,0.05)",
            }}
          >
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/60 to-transparent" />

            {/* Concierge-style progress indicator — 4-step audit journey */}
            <div className="mb-6 fade-up" data-testid="audit-progress">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="text-[10px] tracking-[0.34em] uppercase font-medium text-sapphire-glow"
                    style={{ textShadow: "0 0 12px rgba(96,165,250,0.55)" }}>
                    STEP 1 OF 4
                  </div>
                  <div className="h-px w-8 bg-gradient-to-r from-sapphire-glow/70 to-transparent" />
                  <div className="text-[11px] tracking-[0.18em] uppercase text-platinum/60 font-medium">
                    Tell us about your operation
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-platinum/55">
                  <Clock3 className="w-3 h-3 text-sapphire-glow" />
                  <span>~3 minutes</span>
                </div>
              </div>
              {/* 4-step dot progress with first node active */}
              <div className="flex items-center gap-2">
                {[
                  { active: true,  label: "Your operation" },
                  { active: false, label: "Diagnostic scoring" },
                  { active: false, label: "Private report" },
                  { active: false, label: "Recommendations" },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2 flex-1">
                    <div className="relative shrink-0">
                      <div className="w-2 h-2 rounded-full"
                        style={{
                          background: s.active ? "#60A5FA" : "rgba(255,255,255,0.18)",
                          boxShadow: s.active ? "0 0 12px rgba(96,165,250,0.95), 0 0 22px rgba(96,165,250,0.55)" : "none",
                        }} />
                      {s.active && (
                        <div className="absolute inset-[-6px] rounded-full pointer-events-none ripple-ring"
                          style={{ border: "1px solid rgba(96,165,250,0.6)" }} aria-hidden />
                      )}
                    </div>
                    <div className="h-px flex-1"
                      style={{
                        background: i === 0
                          ? "linear-gradient(90deg, rgba(96,165,250,0.55), rgba(96,165,250,0.10))"
                          : "rgba(255,255,255,0.08)",
                      }} />
                  </div>
                ))}
              </div>
              <div className="mt-2 grid grid-cols-4 gap-2 text-[10px] tracking-[0.06em] text-platinum/45">
                <span className="text-platinum/80">Your operation</span>
                <span>Diagnostic scoring</span>
                <span>Private report</span>
                <span>Recommendations</span>
              </div>
            </div>

            {/* Helper row */}
            <div className="flex items-center gap-3 text-[14px] sm:text-[15px]">
              <Clock3 className="w-4 h-4 text-sapphire-glow shrink-0" />
              <span className="text-sapphire-glow font-medium">Takes about three minutes.</span>
              <span className="text-platinum/65">The more precise your answers, the sharper your report.</span>
            </div>

            {/* Tally embed */}
            <div
              className="mt-5 rounded-xl"
              style={{
                border: "1px dashed rgba(148,163,184,0.25)",
                background: "rgba(10,15,26,0.5)",
              }}
            >
              <iframe
                data-tally-src="https://tally.so/embed/kdgyPd?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1"
                loading="lazy"
                width="100%"
                height="620"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="Esthera Audit — Tally Form"
                data-testid="audit-tally-iframe"
                style={{ display: "block", border: 0, borderRadius: "12px", background: "transparent" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─────── CREDIBILITY LINE (with neon building logo) ─────── */}
      <section className="relative pb-20 lg:pb-24">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-center gap-4 text-center fade-up">
          <div className="relative w-9 h-9 shrink-0">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: "radial-gradient(closest-side, rgba(96,165,250,0.45), transparent 70%)",
                filter: "blur(8px)",
              }}
              aria-hidden
            />
            <img
              src={AUDIT_IMAGES.buildingLogo}
              alt=""
              aria-hidden="true"
              className="relative w-full h-full object-contain breathe-glow"
              style={{ mixBlendMode: "screen" }}
              draggable="false"
            />
          </div>
          <p className="text-[14px] sm:text-[15.5px] text-platinum/75 leading-[1.6]">
            Built for luxury and high-ticket real estate operators across India and Dubai.
          </p>
          <Building2 className="w-4 h-4 text-sapphire-glow/40 hidden" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
